import { Injectable } from '@angular/core';
import dayjs from 'dayjs';
import { NotificationDateGroup, NotificationDateGroupType } from '../models/notification-date-group';
import { NotificationItem } from '../models/notification-item';

@Injectable({ providedIn: 'root' })
export class NotificationsGroupingService {
    public groupNotifications(notifications: NotificationItem[], isDefaultSorting = true): NotificationDateGroup[] {
        const today = dayjs().startOf('day').toDate();
        const yesterday = dayjs(today).add(-1, 'day').toDate();

        const todayNotifications = notifications.filter(n => this.isSameDay(n.notificationDate, today));
        const yesterdayNotifications = notifications.filter(n => this.isSameDay(n.notificationDate, yesterday));
        const olderNotifications = notifications.filter(n => dayjs(n.notificationDate).isBefore(dayjs(yesterday)));

        const groups = [
            {
                type: NotificationDateGroupType.Today,
                items: this.groupByCustomerAndType(todayNotifications, isDefaultSorting),
            },
            {
                type: NotificationDateGroupType.Yesterday,
                items: this.groupByCustomerAndType(yesterdayNotifications, isDefaultSorting),
            },
            {
                type: NotificationDateGroupType.Older,
                items: this.groupByCustomerAndType(olderNotifications, isDefaultSorting),
            }
        ].filter(g => g.items.length > 0);

        if (groups.length === 1 && groups[0].type === NotificationDateGroupType.Older) {
            groups[0].type = NotificationDateGroupType.Latest;
        }

        return groups;
    }

    private groupByCustomerAndType(notifications: NotificationItem[], isDefaultSorting = true): NotificationItem[] {
        const groupedMap = new Map<string, NotificationItem[]>();

        notifications.forEach(notification => {
            const groupKey = this.getGroupingKey(notification);

            if (!groupedMap.has(groupKey)) {
                groupedMap.set(groupKey, []);
            }

            groupedMap.get(groupKey)?.push(notification);
        });

        return Array.from(groupedMap.entries())
            .map(([, notificationsList]) => {
                if (notificationsList.length === 1) {
                    return notificationsList[0];
                }

                const sortedNotifications = notificationsList.sort((a, b) =>
                    isDefaultSorting
                        ? dayjs(b.notificationDate).valueOf() - dayjs(a.notificationDate).valueOf()
                        : dayjs(a.notificationDate).valueOf() - dayjs(b.notificationDate).valueOf()
                );

                const mostRecent = sortedNotifications[0];

                const notificationWithPrimaryCustomer = sortedNotifications.find(n =>
                    n.groupTitle && n.fullName === n.groupTitle
                );

                if (notificationWithPrimaryCustomer) {
                    return {
                        ...mostRecent,
                        imageUrl: notificationWithPrimaryCustomer.imageUrl,
                        firstName: notificationWithPrimaryCustomer.firstName,
                        lastName: notificationWithPrimaryCustomer.lastName,
                        fullName: notificationWithPrimaryCustomer.fullName,
                        childItems: sortedNotifications
                    };
                }

                if (mostRecent.primaryCustomerAvatarUrl && mostRecent.groupTitle) {
                    return {
                        ...mostRecent,
                        imageUrl: mostRecent.primaryCustomerAvatarUrl,
                        firstName: mostRecent.primaryCustomerFirstName,
                        lastName: mostRecent.primaryCustomerLastName,
                        fullName: mostRecent.groupTitle,
                        childItems: sortedNotifications
                    };
                }

                return {
                    ...mostRecent,
                    childItems: sortedNotifications
                };
            })
            .sort((a, b) => {
                return isDefaultSorting
                    ? dayjs(b.notificationDate).valueOf() - dayjs(a.notificationDate).valueOf()
                    : dayjs(a.notificationDate).valueOf() - dayjs(b.notificationDate).valueOf();
            });
    }

    private getGroupingKey(notification: NotificationItem): string {
        if (notification.isOpenHouse) {
            return 'OPEN_HOUSE_GROUP';
        }

        if (notification.isNewMatch) {
            return 'NEW_MATCHES_GROUP';
        }

        if (notification.isCustomer && (!!notification.collaborationId || notification.customerId)) {
            return `CUSTOMER_${notification.collaborationId ? notification.collaborationId : notification.customerId}`;
        }

        return notification.id.toString();
    }

    private isSameDay(date1: Date, date2: Date): boolean {
        return dayjs(date1).isSame(dayjs(date2), 'day');
    }
}